// Operacional — vendas hoje + gráficos + alertas + integrações + tarefas.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, type AlertRow, type IntegrationRow, type TaskRow } from "../api.ts";
import { StatusDot } from "../components/StatusDot.tsx";
import { BarChart, Sparkline } from "../components/charts/index.ts";
import { fmtBRLFull, fmtDateTime, fmtDate, fmtNum } from "../lib/format.ts";

interface TodayPayload {
  today: { revenue: number; orders: number; aov: number };
  baseline_same_dow_28d: { avg_revenue: number; avg_orders: number };
  latest_orders: Array<{ external_id: string; total: number; created_at: string; customer_name: string | null }>;
}

interface HourlyRow { hour: number; orders: number; revenue: number }
interface SparkRow  { date: string; revenue: number; orders: number; sessions: number; organic_sessions: number }

const TYPE_LABEL: Record<string, string> = {
  iset: "iSET", erp: "ERP", matomo: "Matomo",
  search_console: "Search Console", ga4: "GA4",
  google_ads: "Google Ads", meta_ads: "Meta Ads",
};

export function OperationalPage() {
  const { slug } = useParams<{ slug: string }>();
  const [today, setToday] = useState<TodayPayload | null>(null);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [sparks, setSparks] = useState<SparkRow[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[] | null>(null);
  const [integ, setInteg] = useState<{ configured: IntegrationRow[]; available: string[] } | null>(null);
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);

  useEffect(() => {
    if (!slug) return;
    api.get<TodayPayload>(`/api/t/${slug}/kpis/today`).then(setToday).catch(() => {});
    api.get<{ series: HourlyRow[] }>(`/api/t/${slug}/kpis/hourly-today`).then((d) => setHourly(d.series)).catch(() => {});
    api.get<{ series: SparkRow[] }>(`/api/t/${slug}/kpis/sparklines`).then((d) => setSparks(d.series)).catch(() => {});
    api.get<{ alerts: AlertRow[] }>(`/api/t/${slug}/alerts?unack=true&limit=20`).then((d) => setAlerts(d.alerts)).catch(() => {});
    api.get<{ configured: IntegrationRow[]; available: string[] }>(`/api/t/${slug}/integrations`).then(setInteg).catch(() => {});
    api.get<{ tasks: TaskRow[] }>(`/api/t/${slug}/tasks?status=pending`).then((d) => setTasks(d.tasks.slice(0, 8))).catch(() => {});
  }, [slug]);

  return (
    <>
      <div className="section-head">
        <div>
          <p className="eyebrow">Operacional · hoje</p>
          <h1>O que tá rolando agora</h1>
        </div>
      </div>

      <div className="quadrants">
        {/* Vendas hoje */}
        <div className="card">
          <h3>Vendas hoje</h3>
          {today ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                <div>
                  <div className="label" style={{ fontFamily: "var(--f-mono)", fontSize: ".7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".12em" }}>Receita</div>
                  <div style={{ fontFamily: "var(--f-display)", fontSize: "1.7rem", fontWeight: 600 }}>{fmtBRLFull(today.today.revenue)}</div>
                  <div style={{ fontSize: ".75rem", color: "var(--text-muted)" }}>
                    média mesma DOW 4 semanas: {fmtBRLFull(today.baseline_same_dow_28d.avg_revenue)}
                  </div>
                </div>
                <div>
                  <div className="label" style={{ fontFamily: "var(--f-mono)", fontSize: ".7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".12em" }}>Pedidos</div>
                  <div style={{ fontFamily: "var(--f-display)", fontSize: "1.7rem", fontWeight: 600 }}>{fmtNum(today.today.orders)}</div>
                  <div style={{ fontSize: ".75rem", color: "var(--text-muted)" }}>
                    média DOW: {fmtNum(Math.round(today.baseline_same_dow_28d.avg_orders))}
                  </div>
                </div>
              </div>
              {hourly.length > 0 && hourly.some((h) => h.orders > 0) && (
                <div style={{ marginTop: "1rem" }}>
                  <div style={{
                    fontFamily: "var(--f-mono)", fontSize: ".7rem", color: "var(--text-muted)",
                    textTransform: "uppercase", letterSpacing: ".12em", marginBottom: ".5rem"
                  }}>Pedidos por hora · hoje</div>
                  <BarChart
                    data={hourly.map((h) => ({ label: String(h.hour).padStart(2, "0") + "h", value: h.orders }))}
                    height={140}
                    showAxis={true}
                  />
                </div>
              )}
              {sparks.length > 1 && (
                <div style={{ marginTop: "1rem" }}>
                  <div style={{
                    fontFamily: "var(--f-mono)", fontSize: ".7rem", color: "var(--text-muted)",
                    textTransform: "uppercase", letterSpacing: ".12em", marginBottom: ".3rem"
                  }}>Receita · últimos 30 dias</div>
                  <Sparkline data={sparks.map((s) => s.revenue)} height={48} trend="up" />
                </div>
              )}
              <h3 style={{ fontSize: "1rem", marginTop: "1rem" }}>Últimos pedidos</h3>
              {today.latest_orders.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: ".88rem" }}>nenhum pedido ainda hoje</p>
              ) : (
                today.latest_orders.map((o) => (
                  <div key={o.external_id} className="list-row">
                    <span className="mono" style={{ fontSize: ".82rem" }}>#{o.external_id} · {o.customer_name ?? "—"}</span>
                    <span>{fmtBRLFull(o.total)} <span className="mono" style={{ opacity: .55, fontSize: ".78rem" }}>{fmtDateTime(o.created_at)}</span></span>
                  </div>
                ))
              )}
            </>
          ) : <div className="center-loading" style={{ minHeight: 100 }}>carregando…</div>}
        </div>

        {/* Alertas */}
        <div className="card">
          <h3>Alertas (24h)</h3>
          {alerts === null ? (
            <div className="center-loading" style={{ minHeight: 80 }}>carregando…</div>
          ) : alerts.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: ".88rem" }}>tudo verde — sem alertas pendentes.</p>
          ) : (
            alerts.map((a) => (
              <div key={a.id} className="list-row" style={{ alignItems: "flex-start" }}>
                <span>
                  <span className={`pill ${a.severity === "critical" ? "red" : a.severity === "warning" ? "amber" : ""}`} style={{ marginRight: ".5rem" }}>
                    {a.severity}
                  </span>
                  <strong>{a.title}</strong>
                  {a.detail && <div style={{ color: "var(--text-soft)", fontSize: ".82rem" }}>{a.detail}</div>}
                </span>
                <span className="mono" style={{ fontSize: ".75rem", opacity: .6 }}>{fmtDateTime(a.created_at)}</span>
              </div>
            ))
          )}
        </div>

        {/* Status integrações */}
        <div className="card">
          <h3>Status das integrações</h3>
          {integ === null ? (
            <div className="center-loading" style={{ minHeight: 80 }}>carregando…</div>
          ) : integ.configured.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: ".88rem" }}>
              Nenhuma integração configurada. <Link to={`/t/${slug}/settings/integrations`}>Configurar →</Link>
            </p>
          ) : (
            integ.configured.map((i) => (
              <div key={i.id} className="list-row">
                <span>
                  <StatusDot status={i.status} />
                  <strong>{TYPE_LABEL[i.type] ?? i.type}</strong>
                  {i.last_error && <div style={{ color: "var(--red)", fontSize: ".78rem", marginLeft: "1.5rem" }}>{i.last_error}</div>}
                </span>
                <span className="mono" style={{ fontSize: ".78rem", opacity: .6 }}>{fmtDateTime(i.last_success_at)}</span>
              </div>
            ))
          )}
        </div>

        {/* Tarefas pendentes */}
        <div className="card">
          <h3>Tarefas pendentes</h3>
          {tasks === null ? (
            <div className="center-loading" style={{ minHeight: 80 }}>carregando…</div>
          ) : tasks.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: ".88rem" }}>nenhuma tarefa em aberto. <Link to={`/t/${slug}/settings/tasks`}>Adicionar →</Link></p>
          ) : (
            tasks.map((t) => (
              <div key={t.id} className="list-row">
                <span>
                  <strong>{t.title}</strong>
                  {t.front && <span className="pill" style={{ marginLeft: ".5rem" }}>{t.front}</span>}
                  {t.owner && <span className="mono" style={{ fontSize: ".75rem", marginLeft: ".5rem", opacity: .55 }}>{t.owner}</span>}
                </span>
                <span className="mono" style={{ fontSize: ".78rem", opacity: .6 }}>{fmtDate(t.due_date)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
